import { Check } from 'flowbite-react-icons/outline'
import { useRef } from 'react'

import { TooltipBubble } from '@/components/common/Tooltip'
import { useIsTruncated } from '@/components/common/useIsTruncated'
import { useTooltipTrigger } from '@/components/common/useTooltipTrigger'
import { useCopyToClipboard } from '@/utils/clipboard'

interface CopyableValueProps {
  /** Raw value written to the clipboard (may differ from the displayed children). */
  copyValue: string
  /** Accessible action label, e.g. "Copy MRN" / "Copy order number". */
  ariaLabel: string
  /** Rendered display content (formatted / truncated presentation). */
  children: React.ReactNode
  /** Extra classes for the trigger button. */
  className?: string
  /** How long the "Copied" confirmation stays visible (ms). */
  resetMs?: number
  /**
   * Ellipsise the displayed value when it does not fit its container instead of
   * overflowing it. Needed in the order header, which Epic can render in a
   * ~350px sidebar.
   */
  truncate?: boolean
  /**
   * Plain-text form of `children`, revealed in the tooltip when `truncate` is on
   * and the value is actually clipped. Without this the full value would be
   * unreadable (the clipboard still holds it, but the user cannot see it).
   */
  overflowLabel?: string
}

/**
 * Click-to-copy wrapper for banner values (MRN, Order #). Renders a real
 * `<button>` for keyboard + screen-reader support, copies the raw `copyValue`
 * (not the displayed string), and shows a transient "Copied" check announced
 * via an `aria-live="polite"` region. Only render this when a real value
 * exists — callers must guard placeholder/empty states.
 *
 * A small styled tooltip (hover/focus) reads "Click to copy" by default and
 * flips to "Copied" while the copied flag is set; it hides on pointer-leave /
 * blur so the "Copied" confirmation stays only as long as the pointer/focus
 * remains on the trigger. When `truncate` is on and the value is clipped, the
 * tooltip also shows the full value above the "Click to copy" hint.
 */
export function CopyableValue({
  copyValue,
  ariaLabel,
  children,
  className,
  resetMs = 1500,
  truncate = false,
  overflowLabel,
}: CopyableValueProps) {
  const { copied, copy } = useCopyToClipboard(resetMs)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const valueRef = useRef<HTMLSpanElement>(null)
  const isClipped = useIsTruncated(valueRef)
  // Never disabled: unlike plain truncated text this trigger always has
  // something to say ("Click to copy"), so hover/focus is always meaningful.
  const { open, tooltipId, triggerProps } = useTooltipTrigger()

  const showFullValue = truncate && isClipped && Boolean(overflowLabel)

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={() => void copy(copyValue)}
      {...triggerProps}
      aria-label={ariaLabel}
      className={`group relative -mx-1 inline-flex cursor-pointer items-center gap-1 rounded-base px-1 text-left transition-colors hover:bg-background-gray dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
        truncate ? 'min-w-0 max-w-full' : ''
      } ${className ?? ''}`}
    >
      <span ref={valueRef} className={truncate ? 'block truncate' : undefined}>
        {children}
      </span>
      {copied ? <Check className="h-3.5 w-3.5 shrink-0 text-success-600" aria-hidden /> : null}
      {open ? (
        <TooltipBubble id={tooltipId} anchorRef={buttonRef} placement="top">
          {copied ? (
            'Copied'
          ) : showFullValue ? (
            <>
              <span className="block font-semibold">{overflowLabel}</span>
              <span className="block text-white/70">Click to copy</span>
            </>
          ) : (
            'Click to copy'
          )}
        </TooltipBubble>
      ) : null}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? 'Copied' : ''}
      </span>
    </button>
  )
}
